import React, { useState, useEffect, useRef } from "react";
import { GetData } from "../../services/api";
import DataTable from "react-data-table-component";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Button,
  Input,
  InputGroup,
  InputGroupText,
  CardHeader,
  CardTitle,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Loader from "../../components/Common/Loader";
import moment from "moment";
import { Link } from "react-router-dom";
import "./events.css";

const Events = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [filters, setFilters] = useState({
    eventType: "",
    channel: "",
    contactId: "",
    workflowId: "",
    dateFrom: "",
    dateTo: "",
    search: "",
  });
  const itemsPerPage = 50;
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [currentPage, filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("limit", itemsPerPage.toString());
      params.append("offset", (currentPage * itemsPerPage).toString());
      
      if (filters.eventType) params.append("eventType", filters.eventType);
      if (filters.channel) params.append("channel", filters.channel);
      if (filters.contactId) params.append("contactId", filters.contactId);
      if (filters.workflowId) params.append("workflowId", filters.workflowId);
      if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
      if (filters.dateTo) params.append("dateTo", filters.dateTo);
      
      const response = await GetData(`/timeline/activities?${params.toString()}`);
      
      if (response && response.data) {
        setData(response.data.data || []);
        setTotal(response.data.total || 0);
      } else {
        setData([]);
        setTotal(0);
      }
    } catch (err) {
      console.error("Error fetching activities:", err);
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setCurrentPage(0); // Reset to first page
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    handleFilterChange("search", value);
    
    // Debounce search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      // Search will be handled by filtering the displayed data
      // or you can add a search endpoint in the backend
    }, 500);
  };

  const clearFilters = () => {
    setFilters({
      eventType: "",
      channel: "",
      contactId: "",
      workflowId: "",
      dateFrom: "",
      dateTo: "",
      search: "",
    });
    setCurrentPage(0);
  };

  const getEventTypeBadge = (row) => {
    const eventType = row?.eventType;
    const stepType = row?.metadata?.stepType || row?.stepType;
    const typeMap = {
      // Email events
      'email.sent': { color: 'primary', text: 'Email Enviado', icon: 'ri-mail-send-line' },
      'email.delivered': { color: 'success', text: 'Email Entregado', icon: 'ri-mail-check-line' },
      'email.opened': { color: 'info', text: 'Email Abierto', icon: 'ri-eye-line' },
      'email.clicked': { color: 'warning', text: 'Link Clickeado', icon: 'ri-links-line' },
      'email.bounced': { color: 'danger', text: 'Email Rebotado', icon: 'ri-error-warning-line' },
      'email.complained': { color: 'danger', text: 'Marcado como Spam', icon: 'ri-spam-line' },
      'email.failed': { color: 'danger', text: 'Email Fallido', icon: 'ri-mail-close-line' },

      // Workflow events: mostrar tipo concreto según stepType (add_to_list → Agregado a Lista, assign_to_sales_rep → Asignado a asesor)
      'workflow.started': { color: 'primary', text: 'Workflow Iniciado', icon: 'ri-play-line' },
      'workflow.step_completed': stepType === 'add_to_list' ? { color: 'primary', text: 'Agregado a Lista', icon: 'ri-list-add' } : stepType === 'assign_to_sales_rep' ? { color: 'info', text: 'Asignado a asesor', icon: 'ri-user-shared-line' } : { color: 'success', text: 'Paso Completado', icon: 'ri-check-line' },
      'workflow.step.completed': stepType === 'add_to_list' ? { color: 'primary', text: 'Agregado a Lista', icon: 'ri-list-add' } : stepType === 'assign_to_sales_rep' ? { color: 'info', text: 'Asignado a asesor', icon: 'ri-user-shared-line' } : { color: 'success', text: 'Paso Completado', icon: 'ri-check-line' },
      'workflow.step.failed': { color: 'danger', text: 'Paso Fallido', icon: 'ri-error-warning-line' },
      'workflow.failed': { color: 'danger', text: 'Workflow Fallido', icon: 'ri-error-warning-line' },
      'workflow.paused': { color: 'warning', text: 'Workflow Pausado', icon: 'ri-pause-line' },
      'workflow.completed': { color: 'success', text: 'Workflow Completado', icon: 'ri-check-double-line' },

      // Contact events
      'contact_created': { color: 'success', text: 'Contacto Creado', icon: 'ri-user-add-line' },
      'contact_updated': { color: 'info', text: 'Contacto Actualizado', icon: 'ri-user-settings-line' },
      'contact_added_to_list': { color: 'primary', text: 'Agregado a Lista', icon: 'ri-list-add' },
      'contact_removed_from_list': { color: 'secondary', text: 'Removido de Lista', icon: 'ri-list-remove' },

      // Webhook events
      'webhook.executed': { color: 'info', text: 'Webhook Ejecutado', icon: 'ri-webhook-line' },
      'webhook.success': { color: 'success', text: 'Webhook Exitoso', icon: 'ri-check-line' },
      'webhook.failed': { color: 'danger', text: 'Webhook Fallido', icon: 'ri-error-warning-line' },
    };

    const eventInfo = typeMap[eventType] || {
      color: 'secondary',
      text: eventType || 'Desconocido',
      icon: 'ri-question-line'
    };

    return (
      <Badge color={eventInfo.color} className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
        <i className={eventInfo.icon}></i>
        {eventInfo.text}
      </Badge>
    );
  };

  const getChannelBadge = (channel) => {
    const channelMap = {
      email: { color: 'primary', icon: 'ri-mail-line' },
      whatsapp: { color: 'success', icon: 'ri-whatsapp-line' },
      sms: { color: 'info', icon: 'ri-message-line' },
      webhook: { color: 'warning', icon: 'ri-webhook-line' },
      workflow: { color: 'secondary', icon: 'ri-flow-chart-line' },
    };

    const channelInfo = channelMap[channel] || { color: 'secondary', icon: 'ri-question-line' };

    return (
      <Badge color={channelInfo.color} className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
        <i className={channelInfo.icon}></i>
        {channel || 'Desconocido'}
      </Badge>
    );
  };

  /** Convierte slug a texto legible (ej: nuevo_lead_diagnostico → Nuevo lead diagnóstico). */
  const formatLabel = (slug) => {
    if (!slug || typeof slug !== 'string') return '';
    return slug
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .trim();
  };

  /** Tipo de paso del workflow → texto legible (asignación a equipo, envío de correo, etc.). */
  const STEP_TYPE_LABELS = {
    send_email: 'Envío de correo',
    send_email_html: 'Envío de correo',
    add_to_list: 'Agregar a lista',
    assign_to_sales_rep: 'Asignación a equipo',
    wait: 'Espera',
    condition: 'Condición',
    webhook: 'Webhook',
  };
  const getStepTypeLabel = (activity) => {
    const raw = activity.metadata?.stepType || activity.stepType;
    if (!raw || typeof raw !== 'string') return null;
    return STEP_TYPE_LABELS[raw] || formatLabel(raw);
  };

  /** Workflow name from API, metadata o relación resuelta. */
  const getWorkflowName = (activity) =>
    activity.name || activity.metadata?.workflowName || activity.workflow?.name || (activity.workflowId ? String(activity.workflowId) : null);
  /** Step name from API or metadata. */
  const getStepName = (activity) =>
    activity.stepName || activity.metadata?.stepName || null;

  /** Texto corto para columna "Workflow / Paso": sin repetir, legible para no técnicos. */
  const getWorkflowStepDisplay = (activity) => {
    const w = getWorkflowName(activity);
    const s = getStepName(activity);
    const workflowLabel = w ? formatLabel(w) : '';
    const stepLabel = s ? formatLabel(s) : '';
    const actionLabel = getStepTypeLabel(activity);
    // No repetir: si el paso es igual al workflow o está vacío, solo mostrar workflow
    let workflow = '';
    let step = null;
    if (workflowLabel && stepLabel && stepLabel !== workflowLabel) {
      workflow = workflowLabel;
      step = stepLabel;
    } else {
      workflow = workflowLabel || stepLabel;
    }
    if (!workflow && !step && !actionLabel) return null;
    return { workflow: workflow || null, step: step || null, action: actionLabel };
  };

  /** Detalles del evento: solo información adicional (workflow/paso ya va en su columna). */
  const getEventDetails = (activity) => {
    const metadata = activity.metadata || {};
    const parts = [];

    if (activity.eventType === 'contact_added_to_list') {
      if (activity.list?.name) {
        return `Lista: ${activity.list.name}`;
      }
      if (metadata.listName) return `Lista: ${metadata.listName}`;
      if (metadata.listId) return `Lista: ${metadata.listId}`;
      return 'Agregado a lista';
    }

    if (activity.eventType === 'email.sent' || activity.eventType === 'email.delivered') {
      return metadata.subject || 'Sin asunto';
    }

    if (activity.eventType === 'email.failed') {
      return metadata.error || metadata.reason || 'No se pudo enviar el correo';
    }

    if (activity.eventType === 'email.opened') {
      const subject = activity.step?.subject || metadata.subject;
      return subject ? `Asunto: ${subject}` : 'Email abierto';
    }

    if (activity.eventType === 'email.clicked') {
      return metadata.linkUrl || metadata.url || 'Link clickeado';
    }

    if (activity.eventType === 'workflow.step.completed' || activity.eventType === 'workflow.step_completed') {
      const stepType = activity.metadata?.stepType || activity.stepType;
      if (stepType === 'add_to_list') {
        const listName = activity.metadata?.listName || activity.list?.name || activity.result?.listName;
        const listId = activity.metadata?.listId || activity.result?.listId;
        if (listName) return `Lista: ${listName}`;
        if (listId) return `Lista: ${listId}`;
        return 'Agregado a lista';
      }
      if (stepType === 'assign_to_sales_rep') {
        const asesorName = activity.metadata?.salesRepName || activity.metadata?.assigneeName || activity.result?.salesRepName;
        if (asesorName) return `Asesor: ${asesorName}`;
        return 'Asignado a asesor';
      }
      return 'Paso completado';
    }

    if (activity.eventType === 'workflow.step.failed' || activity.eventType === 'workflow.failed') {
      return metadata.error || 'Error en el proceso';
    }

    if (activity.eventType === 'workflow.completed') {
      return 'Workflow finalizado';
    }

    // Webhook: mostrar URL, método y error o estado
    if (activity.eventType === 'webhook.failed' || activity.eventType === 'webhook.success' || activity.eventType === 'webhook.completed' || activity.eventType === 'webhook.executed') {
      const url = metadata.url || metadata.webhookUrl || '';
      const method = (metadata.method || metadata.webhookMethod || 'POST').toUpperCase();
      const parts = [];
      if (url) parts.push(`URL: ${url.length > 60 ? url.slice(0, 60) + '…' : url}`);
      if (method) parts.push(`Método: ${method}`);
      if (activity.eventType === 'webhook.failed') {
        const err = metadata.error || metadata.statusText || metadata.reason;
        if (err) parts.push(`Error: ${err}`);
        if (metadata.status) parts.push(`HTTP ${metadata.status}`);
      } else if (metadata.status) {
        parts.push(`HTTP ${metadata.status}`);
      }
      return parts.length > 0 ? parts.join(' • ') : 'Webhook ejecutado';
    }

    return '-';
  };

  const columns = [
    {
      name: <span className="font-weight-bold fs-13">Fecha</span>,
      cell: (row) => (
        <div>
          <div style={{ fontSize: '13px', fontWeight: 600 }}>
            {moment(row.timestamp || row.createdAt).format("DD/MM/YYYY")}
          </div>
          <div style={{ fontSize: '12px', color: '#6c757d' }}>
            {moment(row.timestamp || row.createdAt).format("HH:mm:ss")}
          </div>
        </div>
      ),
      sortable: true,
      width: '120px',
    },
    {
      name: <span className="font-weight-bold fs-13">Contacto</span>,
      cell: (row) => {
        if (row.contact) {
          return (
            <Link to={`/leads/${row.contact.id}`} style={{ textDecoration: 'none' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#E9553E' }}>
                  {`${row.contact.name || ''} ${row.contact.lastName || ''}`.trim() || 'Sin nombre'}
                </div>
                <div style={{ fontSize: '12px', color: '#6c757d' }}>
                  {row.contact.email || row.contact.phone || '-'}
                </div>
              </div>
            </Link>
          );
        }
        return <span className="text-muted">-</span>;
      },
      sortable: false,
      minWidth: '200px',
    },
    {
      name: <span className="font-weight-bold fs-13">Tipo</span>,
      cell: (row) => getEventTypeBadge(row),
      sortable: true,
      minWidth: '180px',
    },
    {
      name: <span className="font-weight-bold fs-13">Workflow / Paso</span>,
      cell: (row) => {
        const display = getWorkflowStepDisplay(row);
        if (!display || (!display.workflow && !display.step && !display.action)) return <span className="text-muted">-</span>;
        return (
          <div style={{ fontSize: '13px', maxWidth: '240px' }}>
            {display.workflow && <div style={{ fontWeight: 600 }}>Workflow: {display.workflow}</div>}
            {display.step && <div style={{ fontSize: '12px', color: '#6c757d' }}>Paso: {display.step}</div>}
            {display.action && <div style={{ fontSize: '12px', color: '#495057' }}>Acción: {display.action}</div>}
          </div>
        );
      },
      sortable: false,
      minWidth: '200px',
    },
    {
      name: <span className="font-weight-bold fs-13">Canal</span>,
      cell: (row) => getChannelBadge(row.channel),
      sortable: true,
      width: '120px',
    },
    {
      name: <span className="font-weight-bold fs-13">Detalles</span>,
      cell: (row) => (
        <div style={{ fontSize: '13px', maxWidth: '300px' }}>
          {getEventDetails(row)}
        </div>
      ),
      sortable: false,
      minWidth: '200px',
    },
  ];

  const pageCount = Math.ceil(total / itemsPerPage);

  return (
    <div className="page-content" style={{ overflowY: 'auto', minHeight: 'calc(100vh - 70px)' }}>
      <Container fluid={true} style={{ paddingBottom: '3rem' }}>
        <Breadcrumbs title="IHubG" breadcrumbItem="Actividad de Contactos" />
        
        <Row>
          <Col>
            <Card>
              <CardHeader>
                <CardTitle tag="h5" className="mb-0">
                  <i className="ri-history-line me-2"></i>
                  Timeline de Actividades
                </CardTitle>
              </CardHeader>
              <CardBody>
                {/* Filtros */}
                <Row className="mb-4">
                  <Col md={12}>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      <InputGroup style={{ width: '250px' }}>
                        <InputGroupText>
                          <i className="ri-search-line"></i>
                        </InputGroupText>
                        <Input
                          type="text"
                          placeholder="Buscar contacto..."
                          value={filters.search}
                          onChange={handleSearchChange}
                        />
                      </InputGroup>

                      <select
                        className="form-select"
                        style={{ width: '200px' }}
                        value={filters.channel}
                        onChange={(e) => handleFilterChange('channel', e.target.value)}
                      >
                        <option value="">Todos los canales</option>
                        <option value="email">Email</option>
                        <option value="whatsapp">WhatsApp</option>
                        <option value="sms">SMS</option>
                        <option value="webhook">Webhook</option>
                        <option value="workflow">Workflow</option>
                      </select>

                      <select
                        className="form-select"
                        style={{ width: '250px' }}
                        value={filters.eventType}
                        onChange={(e) => handleFilterChange('eventType', e.target.value)}
                      >
                        <option value="">Todos los eventos</option>
                        <option value="email.sent">Email Enviado</option>
                        <option value="email.opened">Email Abierto</option>
                        <option value="email.clicked">Link Clickeado</option>
                        <option value="email.bounced">Email Rebotado</option>
                        <option value="contact_created">Contacto Creado</option>
                        <option value="contact_updated">Contacto Actualizado</option>
                        <option value="workflow.started">Workflow Iniciado</option>
                        <option value="workflow.completed">Workflow Completado</option>
                        <option value="webhook.executed">Webhook Ejecutado</option>
                      </select>

                      <Input
                        type="date"
                        style={{ width: '180px' }}
                        value={filters.dateFrom}
                        onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                        placeholder="Desde"
                      />

                      <Input
                        type="date"
                        style={{ width: '180px' }}
                        value={filters.dateTo}
                        onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                        placeholder="Hasta"
                      />

                      <Button
                        color="secondary"
                        outline
                        onClick={clearFilters}
                        style={{ height: '38px' }}
                      >
                        <i className="ri-close-line me-1"></i>
                        Limpiar
                      </Button>
                    </div>
                  </Col>
                </Row>

                {/* Información de resultados */}
                {total > 0 && (
                  <div className="mb-3">
                    <span className="text-muted" style={{ fontSize: "14px" }}>
                      Mostrando <strong>{currentPage * itemsPerPage + 1}</strong> - <strong>{Math.min((currentPage + 1) * itemsPerPage, total)}</strong> de <strong>{total}</strong> actividades
                    </span>
                  </div>
                )}

                {/* Tabla */}
                {loading ? (
                  <Loader size={150} />
                ) : (
                  <>
                    <DataTable
                      data={data}
                      columns={columns}
                      noDataComponent={
                        <div className="text-center py-5">
                          <i className="ri-inbox-line text-muted" style={{ fontSize: "48px" }}></i>
                          <p className="text-muted mt-3 mb-0">No hay actividades disponibles</p>
                        </div>
                      }
                      customStyles={{
                        headRow: {
                          style: {
                            backgroundColor: "#f8f9fa",
                            borderBottom: "2px solid #dee2e6",
                          },
                        },
                        headCells: {
                          style: {
                            fontSize: "13px",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          },
                        },
                        cells: {
                          style: {
                            fontSize: "14px",
                          },
                        },
                      }}
                      pagination={false}
                    />

                    {/* Paginación */}
                    {pageCount > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-4">
                        <div className="text-muted" style={{ fontSize: "13px" }}>
                          Página {currentPage + 1} de {pageCount}
                        </div>
                        <div className="d-flex gap-2">
                          <Button
                            color="primary"
                            disabled={currentPage === 0}
                            onClick={() => setCurrentPage(currentPage - 1)}
                            style={{ backgroundColor: currentPage === 0 ? '#6c757d' : '#E9553E', borderColor: currentPage === 0 ? '#6c757d' : '#E9553E', color: '#fff' }}
                          >
                            <i className="ri-arrow-left-s-line me-1"></i>
                            Anterior
                          </Button>
                          <Button
                            color="primary"
                            disabled={currentPage >= pageCount - 1}
                            onClick={() => setCurrentPage(currentPage + 1)}
                            style={{ backgroundColor: currentPage >= pageCount - 1 ? '#6c757d' : '#E9553E', borderColor: currentPage >= pageCount - 1 ? '#6c757d' : '#E9553E', color: '#fff' }}
                          >
                            Siguiente
                            <i className="ri-arrow-right-s-line ms-1"></i>
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Events;
